import { useEffect, useMemo, useState } from "react";
import { Paintbrush, Home, Hammer, Layers, Brush, ArrowUpRight, Images, ChevronDown } from "lucide-react";
import { SERVICES } from "@/lib/site";
import { openGalleryFolder } from "@/lib/projectCategories";
import { fetchSlideshow, projectImageUrl } from "@/lib/api";
import { ServiceSlideshow } from "@/components/ServiceSlideshow";
import { clarityEvent, clarityTag } from "@/lib/clarity";

const ICONS = { Paintbrush, Home, Hammer, Layers, Brush };

const DetailItem = ({ d, open = true }) => (
  <li className="border-l-2 border-[#D92525] pl-3">
    <div className="font-display font-black text-[#0B132B] text-sm tracking-tight">{d.title}</div>
    <div
      aria-hidden={!open}
      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      }`}
    >
      <p className="overflow-hidden font-body text-sm text-[#4B5563] leading-relaxed [&:not(:empty)]:mt-1">{d.text}</p>
    </div>
  </li>
);

export const Services = () => {
  const [slideshow, setSlideshow] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpanded = (id) => {
    setExpandedId((cur) => (cur === id ? null : id));
    track("service_readmore_click", id);
  };
  const track = (event, id) => {
    clarityTag("service", id);
    clarityEvent(event);
  };
  const openGallery = (svc) => {
    track("service_gallery_click", svc.id);
    openGalleryFolder(svc.galleryCategory);
  };

  useEffect(() => {
    fetchSlideshow().then(setSlideshow).catch((err) => console.error("Slideshow load failed:", err));
  }, []);

  const imagesFor = useMemo(() => {
    return (svc) => {
      const seen = new Set();
      const out = [];
      for (const tag of svc.slideshowTags || []) {
        for (const img of slideshow[tag] || []) {
          if (seen.has(img.id)) continue;
          seen.add(img.id);
          out.push({ id: img.id, src: projectImageUrl(img.id) });
        }
      }
      return out;
    };
  }, [slideshow]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="tjanster"
      data-testid="services-section"
      className="py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.18em] text-[#FFB3B3]">
            <span className="w-8 h-[2px] bg-[#FFB3B3]" /> 01 — Tjänster
          </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-white mt-3 leading-[1]">
              Allt under
              <br />
              <span className="text-[#FFB3B3]">samma tak.</span>
            </h2>
          </div>
          <p className="font-body text-white/80 md:max-w-md text-base">
            Vi kommer hem till dig och tar hand om hela projektet – från första
            besiktning till sista penseldrag. Hederligt hantverk i Eskilstuna med omnejd.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
          {SERVICES.map((svc, idx) => {
            const Icon = ICONS[svc.icon] || Paintbrush;
            const isHero = idx === 0;
            const isStenputs = svc.id === "mosaik";
            const isOpen = expandedId === svc.id;
            const collapsible = svc.id === "fasad" || svc.id === "mosaik";

            const isHalf = svc.id === "fasad" || isStenputs;
            const span = isHero
              ? "md:col-span-4 md:row-span-2"
              : isHalf
              ? "md:col-span-3"
              : "md:col-span-2";
            const mediaHeight = isHero
              ? "h-72 md:h-auto md:flex-1 md:min-h-[20rem]"
              : isStenputs
              ? "h-72 md:h-80 bg-[#0B132B]"
              : isHalf
              ? "h-64 md:h-80"
              : "h-48";

            return (
              <article
                key={svc.id}
                data-testid={`service-card-${svc.id}`}
                className={`brut-card relative overflow-hidden group ${span} flex flex-col`}
              >
                <div className={`relative overflow-hidden ${mediaHeight}`}>
                  <ServiceSlideshow
                    images={imagesFor(svc)}
                    fallback={svc.image}
                    alt={svc.title}
                    fit={isStenputs ? "object-contain" : "object-cover"}
                    testId={`service-slideshow-${svc.id}`}
                  >
                    {svc.galleryCategory && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openGallery(svc); }}
                        data-testid={`service-image-link-${svc.id}`}
                        aria-label={`Öppna ${svc.title}-galleriet`}
                        className="hidden md:inline-flex absolute bottom-4 right-4 z-[4] items-center gap-2 bg-white text-[#0B132B] font-display font-black text-xs tracking-[0.15em] uppercase px-4 py-3 shadow-[6px_6px_0_#D92525] transition-[transform,background-color,color] duration-200 group-hover:scale-105 hover:bg-[#D92525] hover:text-white active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#D92525]/60"
                      >
                        <Images size={14} strokeWidth={2.4} />
                        Se galleri
                      </button>
                    )}
                    {!isStenputs && (
                      <>
                        <div className="absolute top-4 left-4 z-[3] pointer-events-none bg-[#0B132B] text-white p-2.5">
                          <Icon size={20} strokeWidth={2.2} />
                        </div>
                        <div className="absolute top-4 right-4 z-[3] pointer-events-none bg-[#D92525] text-white font-display font-black text-xs tracking-[0.15em] uppercase px-2 py-1">
                          0{idx + 1}
                        </div>
                      </>
                    )}
                  </ServiceSlideshow>
                </div>
                <div className={`p-6 flex flex-col ${isHero ? "flex-1 md:flex-none" : "flex-1"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight text-[#0B132B] min-w-0 flex-1 [overflow-wrap:anywhere]">
                      {svc.title}
                    </h3>
                    {svc.galleryCategory && (
                      <button
                        type="button"
                        onClick={() => openGallery(svc)}
                        data-testid={`service-image-link-mobile-${svc.id}`}
                        aria-label={`Öppna ${svc.title}-galleriet`}
                        className="md:hidden shrink-0 mt-1 inline-flex items-center gap-1 bg-white text-[#0B132B] border-2 border-[#0B132B] font-display font-black text-[10px] tracking-[0.12em] uppercase px-2.5 py-1.5 shadow-[3px_3px_0_#D92525] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_#D92525] transition-[transform,box-shadow] duration-150"
                      >
                        <Images size={12} strokeWidth={2.4} />
                        Se galleri
                      </button>
                    )}
                  </div>
                  <p
                    data-testid={`service-short-${svc.id}`}
                    className="font-body text-[#4B5563] mt-2 leading-relaxed"
                  >
                    {svc.short}
                  </p>
                  {svc.details ? (
                    <ul className="mt-5 space-y-4">
                      {svc.details.map((d) => (
                        <DetailItem key={d.title} d={d} open={!collapsible || isOpen} />
                      ))}
                    </ul>
                  ) : (
                    <ul className="mt-4 grid grid-cols-1 gap-1.5">
                      {svc.bullets.map((b) => (
                        <li key={b} className="font-body text-sm text-[#0B132B] flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-[#D92525]" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {collapsible && (
                    <div id={`service-details-${svc.id}`} data-testid={`service-details-${svc.id}`} data-open={isOpen} className="sr-only" />
                  )}
                  <div className={`mt-4 flex items-center gap-4 flex-wrap ${collapsible ? "justify-between" : "justify-end"}`}>
                    {collapsible && (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(svc.id)}
                        aria-expanded={isOpen}
                        aria-controls={`service-details-${svc.id}`}
                        data-testid={`service-readmore-${svc.id}`}
                        className="inline-flex items-center gap-1.5 min-h-[44px] md:min-h-0 font-display font-bold text-sm text-[#D92525] hover:text-[#0B132B] transition-colors"
                      >
                        {isOpen ? "Visa mindre" : "Visa mer"}
                        <ChevronDown size={16} strokeWidth={2.5} className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                    )}
                    <button
                      data-testid={`service-cta-${svc.id}`}
                      onClick={() => { track("service_offert_click", svc.id); scrollTo("kontakt"); }}
                      className="inline-flex items-center gap-2 min-h-[44px] md:min-h-0 font-display font-bold text-sm text-[#0B132B] hover:text-[#D92525] transition-colors w-fit"
                    >
                      Begär offert <ArrowUpRight size={16} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
